import { Globe2, Network, Smartphone } from 'lucide-react';

// Heglo serves startup founders. Business-specific copy lives in startupData.ts.
export const copy = {
  "en": {
    "nav": [
      "What you get",
      "Startup ideas",
      "How to launch",
      "FAQs"
    ],
    "signIn": "Sign in",
    "start": "Plan your launch",
    "badge": "For tech entrepreneurs",
    "title": "Build your next startup with Heglo.",
    "subtitle": "Launch a booking, commerce, service marketplace, field-operations or consultation platform under your own brand. Connect customers, businesses and your startup team with websites, apps and management tools.",
    "primaryCta": "Discuss your startup",
    "secondaryCta": "Explore startup ideas",
    "trusted": "Your brand · Your platform · Tools for customers and providers",
    "features": [
      [
        "Choose your startup model",
        "Bring providers together in a marketplace or give businesses tools to serve their own customers.",
        Globe2
      ],
      [
        "Make the platform yours",
        "Customize your identity, landing page, services and settings from your administration panel.",
        Network
      ],
      [
        "Connect every side",
        "Choose web and app channels for customers, providers and operations, with tools for your startup team.",
        Smartphone
      ]
    ],
    "workflowEyebrow": "From idea to operation",
    "workflowTitle": "Choose. Customize. Launch.",
    "workflow": [
      [
        "01",
        "Define your startup",
        "Choose the market, business model and capabilities you need. The Heglo team creates your platform and enables its modules and channels."
      ],
      [
        "02",
        "Make it your own",
        "Use your admin panel to set your brand, publish your landing page, organize services and onboard providers."
      ],
      [
        "03",
        "Prepare and launch",
        "Configure payments and notifications, test the customer journey, and prepare selected apps for release. Then manage and grow your platform."
      ]
    ],
    "securityEyebrow": "You run the startup",
    "securityTitle": "Your platform connects businesses and their customers.",
    "security": [
      "Your startup has its own identity, catalogue and settings",
      "Each provider manages its services, team and orders",
      "Customers book or order through your enabled channels",
      "Your operating model sets how platform and business responsibilities are shared"
    ],
    "finalTitle": "What startup do you want to build?",
    "finalSubtitle": "Tell us who you want to serve. We’ll identify the modules, customization and launch steps your startup needs.",
    "footer": "Heglo — Build and launch your startup.",
    "home": "Heglo home",
    "menu": "Toggle menu",
    "preview": {
      "label": "Illustrative example",
      "title": "Your booking startup",
      "status": "One platform. Everyone connected.",
      "rows": [
        [
          "You, the founder",
          "Manage the brand and platform"
        ],
        [
          "Businesses",
          "Add services, teams and available times"
        ],
        [
          "Customers",
          "Choose a service and book"
        ],
        [
          "Your startup team",
          "Oversees activity and supports users"
        ]
      ],
      "note": "Heglo provides the foundation. You shape the business and its customer experience."
    },
    "capabilities": {
      "label": "What your startup can include",
      "title": "The building blocks behind your platform.",
      "intro": "Combine the modules and channels your model needs. Explore what each part does for your providers and customers.",
      "example": "For example",
      "items": [
        [
          "Services and quotes",
          "List services and prices, show previous work and manage requests that need a quote.",
          "A maintenance provider reviews the job before sending a price."
        ],
        [
          "Bookings and availability",
          "Manage appointment slots, worker schedules, exceptions and capacity.",
          "A salon offers times based on the selected worker’s schedule."
        ],
        [
          "Products and orders",
          "Manage products, sizes or options, stock, order preparation and returns.",
          "A shop sells the same product in several sizes and tracks stock for each."
        ],
        [
          "Teams and field work",
          "Assign workers, manage service areas and vehicles, and follow order progress.",
          "A cleaning company assigns a team that serves the customer’s area."
        ],
        [
          "Customers and communication",
          "Keep customer and order records together, with conversations, ratings and configured notifications.",
          "A customer shares a photo to explain the work, then rates the completed service."
        ],
        [
          "Payments and balances",
          "Follow payment records, balances, commissions and cash reconciliation with configured payment methods.",
          "A business reviews cash collected by its team and the amount still due."
        ],
        [
          "Plans, packages and offers",
          "Configure subscription plans, service bundles, discounts and offers for the supported business model.",
          "A business groups several services into a package with a clear price."
        ],
        [
          "Websites and identity",
          "Set logos, colours and fonts. Design, preview and publish your platform’s landing page.",
          "Introduce your startup and direct visitors to booking, ordering or provider registration."
        ],
        [
          "Apps and management portals",
          "Give customers, providers and operations teams tools for their role, with dashboards for business and platform managers.",
          "The customer places an order while the field team sees the work assigned to them."
        ],
        [
          "Reports and oversight",
          "Review order activity, financial records and team performance from management dashboards.",
          "A manager checks completed work and outstanding balances."
        ],
        [
          "Multiple platforms and permissions",
          "Manage separate platforms, their businesses, user roles, catalogues and market settings.",
          "One operator runs a home-services brand and a separate consultations brand."
        ],
        [
          "Connections to other services",
          "Configure supported payment, messaging, mapping and other integrations for your project.",
          "Choose a supported payment provider for the market where you operate."
        ]
      ],
      "note": "Availability depends on enabled modules and configuration. Payment accounts, custom domains, app releases and specialist workflows have additional setup requirements."
    },
    "roles": {
      "label": "Tools for every participant",
      "title": "You build the startup. They use your platform.",
      "items": [
        [
          "You, the startup founder",
          "Customize your platform, manage providers and customers, and oversee the business."
        ],
        [
          "Businesses and specialists",
          "Manage their services, products, staff, availability and orders."
        ],
        [
          "Customers",
          "Discover providers, book or order, follow progress and share feedback."
        ],
        [
          "Field and operations teams",
          "Receive assigned work and update its progress."
        ],
        [
          "The Heglo team",
          "Provisions your platform, enables agreed capabilities and supports launch preparation."
        ]
      ]
    },
    "faq": {
      "label": "Before you launch",
      "title": "Questions about building your startup.",
      "items": [
        [
          "Is Heglo for startup founders or individual businesses?",
          "Heglo helps entrepreneurs launch their own platforms. Your startup can serve many businesses and their customers, or operate a managed service network."
        ],
        [
          "Can I launch everything in one click?",
          "Heglo brings the core components together, but a complete launch still requires configuration. We create your platform and enable its modules; you customize it, prepare providers and test the journey. Domains, payments and app-store releases have additional steps."
        ],
        [
          "What can I customize from my admin panel?",
          "Your identity, landing-page content and layout, services, providers, customers and supported settings. New workflows or functionality beyond enabled modules may need development."
        ],
        [
          "What is the difference between my startup and a provider?",
          "Your startup owns and operates the platform. A provider is a business or specialist joining it to serve customers. Heglo’s central administration creates and supports startup platforms."
        ],
        [
          "Do I get customer and provider apps?",
          "Heglo has customer, provider and operations apps, plus web portals. We choose the channels your startup needs. Branding, build configuration, testing and store publishing are separate release steps."
        ],
        [
          "Can every business build its own booking website?",
          "Providers can have branded pages and direct booking links. The startup landing-page designer and a complete website builder for each business are different capabilities; we assess the business-level setup you need."
        ],
        [
          "Can I create a healthcare startup?",
          "Booking and remote-consultation modules can form part of it. They do not by themselves provide a complete clinical system. Healthcare workflows and data requirements must be assessed for the intended service before launch."
        ],
        [
          "How do we begin, and what does it cost?",
          "Tell us your target users, business model and launch market. We identify modules, customization and integrations, then agree the launch scope and cost."
        ]
      ]
    }
  },
  "ar": {
    "nav": [
      "ماذا تحصل عليه؟",
      "أفكار مشاريع",
      "كيف تنطلق؟",
      "أسئلة شائعة"
    ],
    "signIn": "تسجيل الدخول",
    "start": "خطط لإطلاق مشروعك",
    "badge": "لرواد الأعمال التقنية",
    "title": "ابنِ مشروعك الناشئ القادم مع هيجلو.",
    "subtitle": "أطلق منصة للحجوزات أو التجارة أو الخدمات أو العمليات الميدانية أو الاستشارات تحت علامتك. اربط العملاء والأنشطة وفريق مشروعك بالموقع والتطبيقات وأدوات الإدارة.",
    "primaryCta": "حدثنا عن فكرتك",
    "secondaryCta": "استكشف أفكار المشاريع",
    "trusted": "علامتك · منصتك · أدوات لعملائك ومقدمي الخدمات",
    "features": [
      [
        "اختر فكرة مشروعك",
        "اجمع مقدمي الخدمات في سوق واحد، أو وفر للشركات أدوات لخدمة عملائها وإدارة أعمالها.",
        Globe2
      ],
      [
        "اجعل المنصة باسمك",
        "خصص الهوية وصفحة التعريف والخدمات وإعدادات العمل من لوحة إدارتك.",
        Network
      ],
      [
        "اربط جميع الأطراف",
        "اختر قنوات الموقع والتطبيقات للعملاء ومقدمي الخدمات والعمليات، مع أدوات لإدارة مشروعك.",
        Smartphone
      ]
    ],
    "workflowEyebrow": "من الفكرة إلى التشغيل",
    "workflowTitle": "اختر فكرتك. خصص منصتك. واستعد للإطلاق.",
    "workflow": [
      [
        "01",
        "حدد مشروعك",
        "اختر السوق ونموذج العمل والوظائف المطلوبة. ينشئ فريق هيجلو منصتك ويفعّل الوحدات والقنوات المناسبة."
      ],
      [
        "02",
        "جهزها باسمك",
        "من لوحة إدارتك، اضبط الهوية، وصمم صفحة منصتك وانشرها، ونظم الخدمات وأضف مقدميها."
      ],
      [
        "03",
        "اختبر ثم انطلق",
        "جهز الدفع والإشعارات، واختبر رحلة العميل، وحضّر التطبيقات المختارة للنشر. ثم تابع مشروعك ونمّه من لوحتك."
      ]
    ],
    "securityEyebrow": "أنت تدير المشروع الناشئ",
    "securityTitle": "منصتك تجمع الأنشطة وعملاءها تحت علامتك.",
    "security": [
      "لمشروعك هوية وخدمات وإعدادات خاصة",
      "كل مقدم خدمة يدير خدماته وفريقه وطلباته",
      "العملاء يحجزون أو يطلبون عبر القنوات المفعلة",
      "لوحتك تتابع مقدمي الخدمات والعملاء وحركة المنصة"
    ],
    "finalTitle": "ما المشروع الذي تريد بناءه؟",
    "finalSubtitle": "حدثنا عمن تريد خدمتهم. نحدد معك الوحدات والتخصيص وخطوات إطلاق مشروعك.",
    "footer": "هيجلو — ابنِ مشروعك الناشئ وأطلق منصتك.",
    "home": "الصفحة الرئيسية لهيجلو",
    "menu": "فتح القائمة أو إغلاقها",
    "preview": {
      "label": "مثال توضيحي",
      "title": "مشروعك: منصة للحجوزات",
      "status": "منصة واحدة تربط الجميع",
      "rows": [
        [
          "أنت، مؤسس المشروع",
          "تدير العلامة والمنصة"
        ],
        [
          "الأنشطة المشتركة",
          "تضيف الخدمات والفرق والمواعيد"
        ],
        [
          "العملاء",
          "يختارون الخدمة ويحجزون"
        ],
        [
          "فريق مشروعك",
          "يتابع النشاط ويدعم المستخدمين"
        ]
      ],
      "note": "هيجلو توفر الأساس، وأنت تشكل مشروعك وتجربة عملائه."
    },
    "capabilities": {
      "label": "ما الذي يمكن أن يشمله مشروعك؟",
      "title": "المكونات التي تبني عليها منصتك.",
      "intro": "اجمع الوحدات والقنوات التي يحتاجها نموذجك. افتح أي قسم لترى فائدته لمقدمي الخدمات والعملاء.",
      "example": "مثال",
      "items": [
        [
          "الخدمات وعروض الأسعار",
          "اعرض خدماتك وأسعارك وأعمالك السابقة، وتابع الطلبات التي تحتاج إلى عرض سعر.",
          "فني صيانة يراجع تفاصيل العمل قبل إرسال السعر للعميل."
        ],
        [
          "الحجوزات والأوقات المتاحة",
          "أدر المواعيد وجداول الموظفين والإجازات وعدد الحجوزات المتاحة.",
          "صالون يعرض الأوقات حسب جدول الموظف الذي اختاره العميل."
        ],
        [
          "المنتجات والطلبات",
          "أضف المنتجات ومقاساتها أو خياراتها، وتابع المخزون وتجهيز الطلبات والمرتجعات.",
          "متجر يعرض منتجًا بعدة مقاسات ويتابع الكمية المتاحة لكل مقاس."
        ],
        [
          "الفرق والعمل الميداني",
          "وزّع العمل، وحدد مناطق الخدمة والمركبات، وتابع تقدم الطلب.",
          "شركة نظافة تختار فريقًا يعمل في منطقة العميل."
        ],
        [
          "العملاء والتواصل",
          "اجمع بيانات العميل وطلباته، مع المحادثات والتقييمات والإشعارات التي تفعلها.",
          "العميل يرسل صورة لتوضيح المشكلة، ثم يقيّم الخدمة بعد إنجازها."
        ],
        [
          "المدفوعات والأرصدة",
          "تابع سجل المدفوعات والأرصدة والعمولات، وراجع المبالغ النقدية المحصلة حسب طرق الدفع المفعلة.",
          "صاحب النشاط يراجع ما حصّله الفريق نقدًا وما تبقى على الطلبات."
        ],
        [
          "الاشتراكات والباقات والعروض",
          "اضبط خطط الاشتراك وباقات الخدمات والخصومات والعروض حسب نموذج نشاطك.",
          "صاحب النشاط يجمع عدة خدمات في باقة بسعر واضح."
        ],
        [
          "المواقع والهوية",
          "حدد الشعار والألوان والخطوط، وصمم صفحة منصتك وعاينها ثم انشرها.",
          "عرّف بمشروعك ووجه الزائر إلى الحجز أو الطلب أو التسجيل كمقدم خدمة."
        ],
        [
          "التطبيقات ولوحات الإدارة",
          "أدوات للعملاء ومقدمي الخدمات وفريق التنفيذ، مع لوحات لأصحاب الأنشطة ومديري المنصات.",
          "العميل يرسل طلبه من التطبيق، والفريق يرى العمل المسند إليه."
        ],
        [
          "التقارير والمتابعة",
          "راجع حركة الطلبات والسجلات المالية وأداء الفريق من لوحات الإدارة.",
          "المدير يراجع الأعمال المنجزة والأرصدة المستحقة."
        ],
        [
          "إدارة المنصات والصلاحيات",
          "أدر منصات مستقلة، والشركات التابعة لها، والمستخدمين والخدمات وإعدادات كل سوق.",
          "جهة واحدة تدير علامة للخدمات المنزلية وأخرى للاستشارات."
        ],
        [
          "الربط بالخدمات الأخرى",
          "اضبط خدمات الدفع والرسائل والخرائط وغيرها من الخدمات التي يدعمها مشروعك.",
          "اختر خدمة دفع مدعومة تناسب البلد الذي تعمل فيه."
        ]
      ],
      "note": "تتحدد الإمكانات بالوحدات والإعدادات المفعلة. لحسابات الدفع والنطاقات الخاصة وإصدارات التطبيقات وسير العمل المتخصص خطوات تجهيز إضافية."
    },
    "roles": {
      "label": "أدوات لكل طرف في مشروعك",
      "title": "أنت تبني المشروع. وهم يستخدمون منصتك.",
      "items": [
        [
          "أنت، مؤسس المشروع",
          "تخصص المنصة وتدير مقدمي الخدمات والعملاء وتتابع مشروعك."
        ],
        [
          "الشركات والمتخصصون",
          "يديرون خدماتهم ومنتجاتهم وموظفيهم ومواعيدهم وطلباتهم."
        ],
        [
          "العملاء",
          "يجدون مقدم الخدمة ويحجزون أو يطلبون ويتابعون التنفيذ ويقيمون التجربة."
        ],
        [
          "فرق التنفيذ والعمليات",
          "تستقبل العمل المسند إليها وتحدث حالته."
        ],
        [
          "فريق هيجلو",
          "ينشئ منصتك ويفعّل الإمكانات المتفق عليها ويدعم تجهيز الإطلاق."
        ]
      ]
    },
    "faq": {
      "label": "قبل إطلاق مشروعك",
      "title": "أسئلة عن بناء منصتك.",
      "items": [
        [
          "هل هيجلو لرواد الأعمال أم للأنشطة الفردية؟",
          "تساعد هيجلو رائد الأعمال على إطلاق منصته الخاصة. يمكن لمشروعك خدمة عدة أنشطة وعملائها، أو تشغيل شبكة خدمات تديرها بنفسك."
        ],
        [
          "هل أطلق كل شيء بضغطة واحدة؟",
          "تجمع هيجلو المكونات الأساسية، لكن الإطلاق الكامل يحتاج إلى تجهيز. ننشئ منصتك ونفعل وحداتها، ثم تخصصها وتجهز مقدمي الخدمات وتختبر الرحلة. للنطاقات والدفع ونشر التطبيقات في المتاجر خطوات إضافية."
        ],
        [
          "ماذا أخصص من لوحة إدارتي؟",
          "هوية المنصة ومحتوى صفحتها وتصميمها والخدمات ومقدميها والعملاء والإعدادات المدعومة. قد يحتاج سير عمل جديد أو وظائف خارج الوحدات المفعلة إلى تطوير."
        ],
        [
          "ما الفرق بين مشروعي ومقدم الخدمة؟",
          "مشروعك يملك المنصة ويديرها. مقدم الخدمة شركة أو متخصص ينضم إليها لخدمة العملاء. وتتولى الإدارة العامة في هيجلو إنشاء منصات المشاريع ودعمها."
        ],
        [
          "هل أحصل على تطبيقات للعملاء ومقدمي الخدمات؟",
          "توجد تطبيقات للعملاء ومقدمي الخدمات والعمليات، مع بوابات ويب. نحدد القنوات التي يحتاجها مشروعك، وتبقى الهوية وإعداد البناء والاختبار والنشر في المتاجر خطوات خاصة بالإصدار."
        ],
        [
          "هل يستطيع كل نشاط بناء موقع حجز خاص؟",
          "يمكن لمقدمي الخدمات امتلاك صفحات بهويتهم وروابط حجز مباشر. مصمم صفحة المشروع الناشئ يختلف عن مصمم مواقع متكامل لكل نشاط داخله؛ نراجع معك تجربة التهيئة المطلوبة لكل نشاط."
        ],
        [
          "هل أستطيع إنشاء مشروع طبي؟",
          "يمكن أن تشكل وحدات الحجز والاستشارات عن بُعد جزءًا منه، لكنها وحدها ليست نظامًا طبيًا متكاملًا. تُقيّم متطلبات الخدمة وسير العمل الطبي والبيانات قبل الإطلاق."
        ],
        [
          "كيف أبدأ، وما التكلفة؟",
          "حدثنا عن عملائك ونموذج مشروعك وسوق الإطلاق. نحدد الوحدات والتخصيص والتكاملات ثم نتفق على نطاق الإطلاق وتكلفته."
        ]
      ]
    }
  }
} as const;
